import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, Coffee, Gift, TrendingUp, Clock } from "lucide-react";
import { buildAdminApi } from "../../lib/admin";

const PERIODS = [
    { id: "today", label: "Today" },
    { id: "7d", label: "Last 7 days" },
    { id: "30d", label: "Last 30 days" },
    { id: "all", label: "All time" },
];

const AdminReports = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [period, setPeriod] = useState("today");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.get(`/admin/reports?period=${period}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, slug]);

    const maxHour = data ? Math.max(...data.by_hour.map((x) => x.count), 1) : 1;
    const maxDay = data ? Math.max(...data.by_day.map((x) => x.revenue), 1) : 1;

    const peakHourLabel = data?.peak_hour !== null && data?.peak_hour !== undefined
        ? `${data.peak_hour % 12 || 12}:00 ${data.peak_hour < 12 ? "AM" : "PM"}`
        : "—";

    return (
        <div>
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                <h1 className="font-display font-black text-3xl">Reports</h1>
                <div className="flex gap-1 rounded-full bg-white border border-[#E8DCCB] p-1">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`period-${p.id}`}
                            onClick={() => setPeriod(p.id)}
                            className={`px-3 py-1.5 rounded-full font-display font-bold text-xs transition-all ${
                                period === p.id ? "bg-[#2C1A12] text-white" : "text-[#5b4438]"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading || !data ? (
                <div className="text-[#8C7A72] py-16 text-center">Loading…</div>
            ) : (
                <>
                    {/* KPI grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <KPI label="Revenue" value={`$${data.revenue.toFixed(2)}`} icon={DollarSign} sub={period} />
                        <KPI label="Orders" value={data.orders} icon={TrendingUp} sub={period} />
                        <KPI label="Coffees" value={data.coffees} icon={Coffee} sub="stamps issued" />
                        <KPI label="Free redeemed" value={data.redemptions} icon={Gift} sub="loyalty" />
                        <KPI label="Avg ticket" value={`$${data.average_order.toFixed(2)}`} sub="per order" />
                        <KPI label="Tips" value={`$${data.tips_total.toFixed(2)}`} sub="collected" />
                        <KPI label="Customers" value={data.unique_customers} sub="unique" />
                        <KPI label="Peak hour" value={peakHourLabel} icon={Clock} sub="busiest" />
                    </div>

                    {/* By hour chart */}
                    <div className="mt-8 rounded-3xl bg-white border border-[#E8DCCB] p-6">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-4">
                            Orders by hour
                        </div>
                        <div className="flex items-end gap-1 h-32">
                            {data.by_hour.map((h) => (
                                <div
                                    key={h.hour}
                                    className="flex-1 flex flex-col items-center gap-1"
                                    title={`${h.hour}:00 — ${h.count} orders`}
                                >
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${(h.count / maxHour) * 100}%` }}
                                        transition={{ delay: h.hour * 0.02 }}
                                        className={`w-full rounded-t-md ${
                                            h.hour === data.peak_hour ? "bg-[#FF4F18]" : "bg-[#2C1A12]/70"
                                        }`}
                                        style={{ minHeight: h.count > 0 ? 4 : 0 }}
                                    />
                                    <div className="text-[9px] text-[#8C7A72] font-semibold">
                                        {h.hour % 3 === 0 ? h.hour : ""}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* By day (7d / 30d only) */}
                    {data.by_day && data.by_day.length > 0 && (
                        <div className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-6">
                            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-4">
                                Daily revenue
                            </div>
                            <div className="flex items-end gap-2 h-32">
                                {data.by_day.map((d) => (
                                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} — $${d.revenue.toFixed(2)}`}>
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: `${(d.revenue / maxDay) * 100}%` }}
                                            className="w-full bg-[#FF4F18] rounded-t-md"
                                            style={{ minHeight: d.revenue > 0 ? 4 : 0 }}
                                        />
                                        <div className="text-[9px] text-[#8C7A72] font-semibold">
                                            {d.day.slice(5)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Payment split */}
                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <Split label="Online (Stripe)" value={data.payment_split.online} total={data.orders} />
                        <Split label="Pay at counter" value={data.payment_split.counter} total={data.orders} />
                    </div>

                    {/* Top items */}
                    <div className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-6">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-3">
                            Top items · {period}
                        </div>
                        <div className="flex flex-col gap-1">
                            {data.top_items.map((it, idx) => (
                                <div key={it.name} className="flex items-center justify-between py-2 border-b border-[#E8DCCB]/60 last:border-0">
                                    <div className="flex items-center gap-3">
                                        <div className="h-7 w-7 rounded-full bg-[#F9F6F0] flex items-center justify-center font-display font-black text-xs">
                                            {idx + 1}
                                        </div>
                                        <div className="font-display font-bold text-sm">{it.name}</div>
                                    </div>
                                    <div className="font-display font-black">{it.qty}</div>
                                </div>
                            ))}
                            {data.top_items.length === 0 && (
                                <div className="text-sm text-[#8C7A72] py-4 text-center">No data for this period.</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const KPI = ({ label, value, sub, icon: Icon }) => (
    <div className="rounded-2xl bg-white border border-[#E8DCCB] p-4 relative overflow-hidden">
        {Icon && (
            <div className="absolute right-3 top-3 opacity-20">
                <Icon className="h-6 w-6" strokeWidth={1.5} />
            </div>
        )}
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">{label}</div>
        <div className="mt-2 font-display font-black text-xl">{value}</div>
        {sub && <div className="text-[10px] text-[#8C7A72] mt-1 uppercase tracking-wider">{sub}</div>}
    </div>
);

const Split = ({ label, value, total }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className="rounded-2xl bg-white border border-[#E8DCCB] p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">{label}</div>
            <div className="mt-2 flex items-baseline gap-2">
                <div className="font-display font-black text-xl">{value}</div>
                <div className="text-xs text-[#8C7A72]">{pct}%</div>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-[#E8DCCB] overflow-hidden">
                <div className="h-full bg-[#FF4F18] transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

export default AdminReports;
